import { useCallback, useMemo, useRef, useState } from "react";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { useAuth } from "@/auth/useAuth";
import { dailyApi } from "@/features/daily/api/daily.api";
import { useDailyQueryState } from "@/features/daily/hooks/use-daily-query-state";
import { useDailyFilters } from "@/features/daily/hooks/use-daily-filters";
import { DailyResourceStatus, useDailyResource } from "@/features/daily/hooks/use-daily-resource";
import { DailyStateBlock, DailySectionState } from "@/features/daily/components/DailyStateBlock";
import { DailyHeaderMeta } from "@/features/daily/components/DailyHeaderMeta";
import { SkipLink } from "@/features/daily/components/SkipLink";
import { AttentionPanel } from "@/features/daily/components/command/AttentionPanel";
import { EventsPanel } from "@/features/daily/components/command/EventsPanel";
import { AlertStrip } from "@/features/daily/components/command/AlertStrip";
import { LiveBar } from "@/features/daily/components/command/LiveBar";
import { PriorityOperations } from "@/features/daily/components/command/PriorityOperations";
import { TodayFunnelCard } from "@/features/daily/components/command/TodayFunnelCard";
import { TodayHourlyCard } from "@/features/daily/components/command/TodayHourlyCard";
import { isStatusTodayDto, todayViewModel } from "@/features/daily/components/command/today-view-model";
import { OperationDetailModal } from "@/features/orders-analytics/components/OperationDetailModal";
import { isQueuePageDto } from "@/features/daily/components/diagnostic/diagnostic-view-model";
import { QuietHourNote } from "@/features/daily/components/command/QuietHourNote";
import { ServicesStrip } from "@/features/daily/components/command/ServicesStrip";
import { TodayOperations } from "@/features/daily/components/command/TodayOperations";
import { commandViewModel, dailyFormat, isPlatformStatusDto } from "@/features/daily/components/command/command-view-model";
import { healthViewModel, isEventsDto } from "@/features/daily/components/command/health-view-model";
import "@/features/daily/styles/daily.css";

const BODY_ID = "daily-command-main";
const POLL_MS = 60_000;
const EVENTS_LIMIT = "20";
const PRIORITY_PAGE_SIZE = 50;

// L44: the resource status maps to a section state exhaustively — a state this map
// forgot could only ever surface as a silently blank section.
const SECTION_STATE: Record<DailyResourceStatus, DailySectionState> = {
    idle: "loading",
    loading: "loading",
    ok: "ok",
    partial: "partial",
    stale: "stale",
    error: "error",
    forbidden: "forbidden",
};

export function CommandCenterPage() {
    const { state: authState } = useAuth();
    const { query, set } = useDailyQueryState();
    // D105: the app's workspace + range bar. Salud stays live ("today") until someone picks a range.
    const { filters, onFiltersChange } = useDailyFilters(query, set, "today");

    const viewerId = authState.user === null ? "" : authState.user.id;
    // D125: the operator can pause the live refresh; the page then asks for nothing on its own.
    const [auto, setAuto] = useState(true);
    const pollMs = auto ? POLL_MS : undefined;

    // L354/FR-009: the live window is asked for by PRESET, never as a timestamp
    // pinned when the page mounted. A tab left open past midnight therefore starts
    // receiving the new day the moment it polls, instead of calling yesterday "today".
    const scopeKey = query.scope.join(",");
    const typesKey = query.types.join(",");
    const params = useMemo(() => ({
        window: query.windowPreset ?? "today",
        from: query.windowPreset === "custom" ? query.windowFrom ?? "" : "",
        to: query.windowPreset === "custom" ? query.windowTo ?? "" : "",
        scope: scopeKey,
        types: typesKey,
    }), [query.windowPreset, query.windowFrom, query.windowTo, scopeKey, typesKey]);

    const eventsParams = useMemo(() => ({
        limit: EVENTS_LIMIT,
        scope: params.scope,
        types: params.types,
    }), [params.scope, params.types]);

    const fetcher = useCallback(
        (signal: AbortSignal) => dailyApi.getStatus(params, { signal }),
        [params],
    );

    const eventsFetcher = useCallback(
        (signal: AbortSignal) => dailyApi.getEvents(eventsParams, { signal }),
        [eventsParams],
    );

    const resource = useDailyResource(
        { resource: "status", scope: params.scope, query: params },
        fetcher,
        { identity: viewerId, isData: isPlatformStatusDto, pollMs },
    );

    // B16 (D134): today hour by hour, its funnel and the operations to look at first.
    const todayParams = useMemo(() => ({ scope: params.scope, types: params.types }), [params.scope, params.types]);
    const todayFetcher = useCallback(
        (signal: AbortSignal) => dailyApi.getStatusToday(todayParams, { signal }),
        [todayParams],
    );
    const todayResource = useDailyResource(
        { resource: "status-today", scope: todayParams.scope, query: todayParams },
        todayFetcher,
        { identity: viewerId, isData: isStatusTodayDto, pollMs },
    );
    // D144: the priority panel fills the column with the first page of `queue=priority`;
    // the five from `/status/today` are the first paint until it arrives.
    const priorityParams = useMemo(() => ({ queue: "priority", size: String(PRIORITY_PAGE_SIZE), scope: params.scope, types: params.types }), [params.scope, params.types]);
    const priorityFetcher = useCallback(
        (signal: AbortSignal) => dailyApi.getQueue(priorityParams, { signal }),
        [priorityParams],
    );
    const priorityResource = useDailyResource(
        { resource: "priority-queue", scope: priorityParams.scope, query: priorityParams },
        priorityFetcher,
        { identity: viewerId, isData: isQueuePageDto, pollMs },
    );
    const [operationId, setOperationId] = useState<string | null>(null);
    const triggerRef = useRef<HTMLElement | null>(null);

    const eventsResource = useDailyResource(
        { resource: "events", scope: eventsParams.scope, query: eventsParams },
        eventsFetcher,
        { identity: viewerId, isData: isEventsDto, pollMs },
    );

    const dto = resource.data;
    const statusVm = dto === null ? null : commandViewModel.status(dto);
    // D36: el permiso viaja CON el payload y lo decide el servidor que lo aplica.
    const canOperate = dto !== null && commandViewModel.canOperate(dto);
    const busy = resource.status === "loading" || resource.status === "idle";
    // FR-024: a background refresh never blanks a section that already has evidence
    // on screen (the Refresh button carries the in-flight feedback instead), but a
    // FAILED refresh must say so while keeping the previous figures and their age.
    const sectionState: DailySectionState = dto !== null && busy ? "ok" : SECTION_STATE[resource.status];

    const hrefFor = (extra: Readonly<Record<string, string>>): string =>
        commandViewModel.diagnosticHref({ scope: params.scope, types: params.types, ...extra });
    const inProgressHref = hrefFor({ queue: "day", filter: "in_progress" });
    const overdueHref = hrefFor({ queue: "overdue", window: params.window, from: params.from, to: params.to });

    const toggleAuto = (): void => {
        // Resuming catches up at once instead of showing figures as old as the pause.
        if (!auto) {
            resource.refresh();
            eventsResource.refresh();
            todayResource.refresh();
            priorityResource.refresh();
        }
        setAuto(!auto);
    };
    const alert = dto === null ? null : healthViewModel.alert(dto);
    // D144 (like D143): the footer's source and evidence notes live in the state pill's (i).
    const sourceNotes = [
        `Consulta realizada: ${dailyFormat.stamp(resource.meta.fetchedAt)}`,
        `Evidencia de: ${dailyFormat.stamp(resource.meta.sourceAsOf)}`,
        ...(resource.meta.stale ? ["La evidencia está desactualizada"] : []),
        ...(statusVm === null ? [] : [`Evidencia: ${statusVm.qualityLabel} · ${statusVm.coverageLabel} · ${statusVm.policyLabel}`]),
    ];
    const today = todayResource.data;
    const priority = priorityResource.data !== null
        ? todayViewModel.priorityPage(priorityResource.data)
        : today === null ? [] : todayViewModel.priority(today);
    const healthy = dto !== null && healthViewModel.isHealthy(dto);

    const events = eventsResource.data === null ? null : healthViewModel.events(eventsResource.data.events);
    const eventsFailed = eventsResource.data === null
        && (eventsResource.status === "error" || eventsResource.status === "forbidden");

    return (
        <Shell title="Command Center" subtitle="Salud de la plataforma">
            <div className={healthy ? "daily-page daily-page--salud daily-page--ok" : "daily-page daily-page--salud"}>
                <SkipLink targetId={BODY_ID} />
                <PageHeader
                    title="Salud de la plataforma"
                    lede="Qué funciona ahora y qué necesita atención."
                    right={(
                        <DailyHeaderMeta
                            chips={null}
                            filters={filters}
                            onFiltersChange={onFiltersChange}
                            busy={busy}
                            onRefresh={resource.refresh}
                        />
                    )}
                />

                <LiveBar
                    fetchedAt={resource.meta.fetchedAt}
                    auto={auto}
                    onToggleAuto={toggleAuto}
                    pollSeconds={POLL_MS / 1000}
                    pill={dto === null ? null : { ...healthViewModel.statusPill(dto), tooltip: [...healthViewModel.statusPill(dto).tooltip, ...sourceNotes] }}
                    counts={dto === null ? null : healthViewModel.causeCounts(dto)}
                />

                <div className="daily-body" id={BODY_ID} tabIndex={-1}>
                    <DailyStateBlock
                        state={sectionState}
                        label="Salud de la plataforma"
                        detail={resource.error === null ? undefined : resource.error.message}
                        onRetry={resource.refresh}
                    >
                        {dto === null ? null : (
                            <div className="daily-health">
                                <div className="daily-health__top">
                                    {alert === null ? null : <AlertStrip alert={alert} />}
                                    <QuietHourNote quiet={healthViewModel.quietHour(dto)} />
                                    <TodayOperations
                                        tiles={healthViewModel.todayTiles(dto, {
                                            overdue: overdueHref,
                                            open: inProgressHref,
                                            historic: hrefFor({ queue: "historic_stuck" }),
                                            backlog: hrefFor({ queue: "credit_backlog" }),
                                        })}
                                        queueHref={inProgressHref}
                                        aside={<ServicesStrip services={healthViewModel.services(commandViewModel.capabilities(dto.capabilities))} />}
                                    />
                                </div>
                                <div className="daily-health__main">
                                    {today === null ? (
                                        // Guardian P3: a failed or refused read says so; the section never just vanishes.
                                        todayResource.status === "error" || todayResource.status === "forbidden" ? (
                                            <DailyStateBlock
                                                state={todayResource.status}
                                                label="Actividad de hoy"
                                                detail={todayResource.error === null ? undefined : todayResource.error.message}
                                                onRetry={todayResource.refresh}
                                            >
                                                {null}
                                            </DailyStateBlock>
                                        ) : null
                                    ) : (
                                        <>
                                            <div className="daily-today-row">
                                                <TodayHourlyCard vm={todayViewModel.hourly(today)} />
                                                <TodayFunnelCard vm={todayViewModel.funnel(today)} />
                                            </div>
                                            <PriorityOperations
                                                rows={priority}
                                                allHref={hrefFor({ queue: "priority" })}
                                                onOpen={(id, trigger) => {
                                                    triggerRef.current = trigger;
                                                    setOperationId(id);
                                                }}
                                            />
                                        </>
                                    )}
                                </div>
                                <div className="daily-health__side">
                                    <AttentionPanel
                                        register={healthViewModel.register(dto)}
                                        incidents={commandViewModel.incidents(dto.incidents, { canOperate, viewerId })}
                                        viewerId={viewerId}
                                        calmNote={healthViewModel.calmNote(dto)}
                                        queueHref={inProgressHref}
                                        onApplied={resource.refresh}
                                        causes={healthViewModel.causes(dto)}
                                    />
                                    <EventsPanel events={events} failed={eventsFailed} />
                                </div>
                            </div>
                        )}
                    </DailyStateBlock>

                </div>
            </div>
            {operationId === null ? null : (
                <OperationDetailModal
                    operationId={operationId}
                    operationIds={priority.map((item) => item.row.operationId)}
                    onNavigate={setOperationId}
                    onClose={() => setOperationId(null)}
                    restoreFocusTo={triggerRef.current}
                />
            )}
        </Shell>
    );
}
